import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

type Language = 'ru' | 'uz' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

/** Extract language from pathname first segment. Returns null if not a lang prefix. */
function getLangFromPath(pathname: string): Language | null {
  const seg = pathname.split('/')[1];
  if (seg === 'ru' || seg === 'en' || seg === 'uz') return seg;
  return null;
}

const SUPPORTED_LANGS: Language[] = ['ru', 'en', 'uz'];

// Dictionary is now split per-language (ru.ts / uz.ts / en.ts) and loaded
// on demand. We only download the active language (~210 KB) instead of all
// three (~630 KB).
import {
  loadDictionary,
  getCachedDictionary,
  subscribeToDictionary,
  getDictionaryVersion,
} from '@/lib/i18n/dictionary';


const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function detectBrowserLanguage(): Language {
  if (typeof navigator === 'undefined') return 'en';

  const raw = (navigator.languages && navigator.languages.length > 0
    ? navigator.languages[0]
    : navigator.language
  )
    ?.toLowerCase()
    .trim();

  if (raw?.startsWith('ru')) return 'ru';
  if (raw?.startsWith('uz')) return 'uz';
  return 'en';
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // URL is the primary source of truth — safe to read window here (no router needed)
    const fromUrl = getLangFromPath(
      typeof window !== 'undefined' ? window.location.pathname : '/'
    );
    if (fromUrl) return fromUrl;
    // Fallback: localStorage, then browser lang
    const saved = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
    return (saved as Language) || detectBrowserLanguage();
  });

  // Sync language on browser navigation (popstate covers back/forward + react-router pushState)
  // Does NOT use useLocation — safe to run above <Router>
  useEffect(() => {
    const syncFromUrl = () => {
      const fromUrl = getLangFromPath(window.location.pathname);
      if (fromUrl) {
        setLanguageState(prev => (fromUrl !== prev ? fromUrl : prev));
      }
    };

    // popstate fires on back/forward navigation
    window.addEventListener('popstate', syncFromUrl);

    // Patch history.pushState / replaceState so SPA navigations also trigger sync
    const origPush = history.pushState.bind(history);
    const origReplace = history.replaceState.bind(history);

    history.pushState = (...args) => {
      origPush(...args);
      syncFromUrl();
    };
    history.replaceState = (...args) => {
      origReplace(...args);
      syncFromUrl();
    };

    return () => {
      window.removeEventListener('popstate', syncFromUrl);
      history.pushState = origPush;
      history.replaceState = origReplace;
    };
  }, []);

  // Persist to localStorage & update <html lang>
  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  // Kick off dictionary load for the active language; re-render when it lands
  // so any t() calls that returned the raw key get the real translation.
  const [, forceRender] = useState(0);
  useEffect(() => {
    if (!getCachedDictionary(language)) {
      void loadDictionary(language);
    }
    const unsub = subscribeToDictionary(() => forceRender(getDictionaryVersion()));
    return unsub;
  }, [language]);

  // setLanguage is kept for compatibility but URL navigation is the canonical way
  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const t = (key: string) => {
    // Primary lookup — current language.
    const dict = getCachedDictionary(language);
    if (dict && dict[key]) return dict[key];

    // Fallback — during a language switch the new dictionary may still be
    // downloading. Instead of showing the raw key (e.g. "nav.home"), look
    // through any already-loaded language so the user sees a real translation
    // for the ~50-150 ms window. As soon as the new dict arrives, the
    // subscription fires forceRender() and the proper translation appears.
    for (const lang of SUPPORTED_LANGS) {
      if (lang === language) continue;
      const fallbackDict = getCachedDictionary(lang);
      if (fallbackDict && fallbackDict[key]) return fallbackDict[key];
    }

    // Last resort — the key itself.
    return key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
};
